import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, TextareaAutosize, Tooltip } from '@material-ui/core'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../../components/commonDesign/Button'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import { uploadDocument } from '../../../../service'
import { useUploadAttachment } from '../../../../api/denaliAssistant'
import { useVoiceInput } from '../../../../hooks/useVoiceInput'
import { generateAttachmentId } from '../utils/attachmentUtils'
import Disclaimer from './Disclaimer'
import AttachmentPreviewList from './AttachmentPreviewList'

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    backgroundColor: 'transparent'
  },
  inputContent: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  },
  inputWrapper: {
    backgroundColor: 'var(--dai-color-input)',
    color: 'var(--font-color-main)',
    border: 'var(--dai-border-accent)',
    boxShadow: 'var(--shadow-sm)',
    borderRadius: '12px',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    position: 'relative',
    '&:focus-within': {
      borderColor: 'var(--dai-color-border-accent-hl)'
    }
  },
  spinningGradientWrapper: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-2px',
      borderRadius: '14px',
      padding: '2px',
      background: 'conic-gradient(from 0deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      animation: '$spinGradient 3s linear infinite',
      pointerEvents: 'none'
    }
  },
  '@keyframes spinGradient': {
    '0%': {
      background: 'conic-gradient(from 0deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '25%': {
      background: 'conic-gradient(from 90deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '50%': {
      background: 'conic-gradient(from 180deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '75%': {
      background: 'conic-gradient(from 270deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '100%': {
      background: 'conic-gradient(from 360deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    }
  },
  dragActive: {
    borderColor: 'var(--dai-color-border-accent-hl)',
    backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 8%, var(--dai-color-input))'
  },
  sending: {
    color: 'color-mix(var(--font-color-main) 50%, transparent)',
    backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 5%, var(--dai-color-input))'
  },
  sendingCompact: {
    minHeight: '32px',
    maxHeight: '32px',
    padding: '0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '& > *': {
      opacity: 0,
      transform: 'scale(0.95)'
    }
  },
  textarea: {
    width: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'inherit',
    fontFamily: 'inherit',
    '&::placeholder': {
      color: 'var(--dai-color-text-low)'
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  tools: {
    display: 'flex',
    gap: '8px'
  },
  toolButton: {
    padding: '6px',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--dai-color-text-low)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d3)',
      color: 'var(--color-primary-500)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  toolButtonListening: {
    color: 'var(--color-danger-500)',
    animation: '$pulse 1.5s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1
    },
    '50%': {
      opacity: 0.6
    }
  },
  charCount: {
    fontSize: '12px',
    color: 'var(--dai-color-text-low)'
  },
  submitSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  keyboardHint: {
    fontSize: '12px',
    color: 'var(--dai-color-text-low)'
  }
}))

const MAX_LENGTH = 4000
const MAX_ATTACHMENTS = 5

const MessageInput = forwardRef(({ onSend, disabled, placeholder, className, style, minRows, submissionMode }, ref) => {
  const classes = useStyles()
  const textareaRef = useRef(null)
  const fileInputRef = useRef(null)
  const uploadAttachmentMutation = useUploadAttachment()
  // Fully self-contained state - no parent re-renders
  const [value, setValue] = React.useState('')
  const [charCount, setCharCount] = React.useState(0)
  const [hasContent, setHasContent] = React.useState(false)
  // Attachment state: { file: File, status: 'uploading' | 'uploaded', s3Key?: string }
  const [attachments, setAttachments] = useState([])
  const [sending, setSending] = useState(false)
  const _disable = useMemo(() => sending || disabled, [sending, disabled])

  // Voice input hook
  const handleTranscriptUpdate = useCallback((newValue) => {
    setValue(newValue)
    setCharCount(newValue.length)
    setHasContent(!!newValue.trim())
  }, [])

  const getCurrentValue = useCallback(() => {
    return textareaRef.current?.value || value
  }, [value])

  const { isListening, isSpeechSupported, handleVoiceToggle } = useVoiceInput({
    getCurrentValue,
    handleTranscriptUpdate,
    maxLength: MAX_LENGTH
  })

  const handleFileAttach = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(async (fileOrEvent) => {
    // Handle both file input events and dropzone file arrays
    let files = []
    if (fileOrEvent?.target?.files) {
      // File input event
      files = Array.from(fileOrEvent.target.files)
    } else if (Array.isArray(fileOrEvent) && fileOrEvent.length > 0) {
      // Dropzone files array
      files = fileOrEvent
    } else if (fileOrEvent instanceof File) {
      // Direct file object
      files = [fileOrEvent]
    }

    // Limit to remaining slots
    const remainingSlots = MAX_ATTACHMENTS - attachments.length
    const filesToAdd = files.slice(0, remainingSlots)

    if (filesToAdd.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    if (filesToAdd.length < files.length) {
      console.warn(`Only ${filesToAdd.length} of ${files.length} files added. Max ${MAX_ATTACHMENTS} attachments allowed.`)
    }

    // Create new attachment objects for each file
    const newAttachments = filesToAdd.map(file => ({
      id: generateAttachmentId(),
      file,
      status: 'uploading',
      fileName: file.name,
      contentType: file.type || 'application/octet-stream'
    }))

    // Add to state immediately (all with uploading status)
    setAttachments(prev => [...prev, ...newAttachments])

    // Upload each file, matching by unique id to avoid index race conditions
    for (let i = 0; i < newAttachments.length; i++) {
      const file = filesToAdd[i]
      const attachmentId = newAttachments[i].id

      try {
        // Get presigned URL from backend
        const data = await uploadAttachmentMutation.mutateAsync({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream'
        })

        const { url, key } = data

        // Upload file to S3 using presigned URL
        await uploadDocument(url, file, undefined, undefined)

        // Update the specific attachment with S3 key
        setAttachments(prev => prev.map(att =>
          att.id === attachmentId
            ? { ...att, status: 'uploaded', s3Key: key }
            : att
        ))
      } catch (error) {
        console.error('Failed to upload file:', error)
        // Mark the attachment as failed
        setAttachments(prev => prev.map(att =>
          att.id === attachmentId
            ? { ...att, status: 'error' }
            : att
        ))
      }
    }

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [uploadAttachmentMutation, attachments.length])

  const addTextAttachment = useCallback(async (text, fileName = 'selected-text.txt') => {
    // Check if we have room for more attachments
    if (attachments.length >= MAX_ATTACHMENTS) return

    // Create a Blob/File from the text
    const blob = new Blob([text], { type: 'text/plain' })
    const file = new File([blob], fileName, { type: 'text/plain' })

    // Add attachment with textContent for preview
    const newAttachment = {
      id: generateAttachmentId(),
      file,
      status: 'uploading',
      fileName,
      contentType: 'text/plain',
      textContent: text // Store original text for preview
    }

    const attachmentId = newAttachment.id

    setAttachments(prev => [...prev, newAttachment])

    try {
      // Get presigned URL from backend
      const data = await uploadAttachmentMutation.mutateAsync({
        fileName,
        contentType: 'text/plain'
      })

      const { url, key } = data

      // Upload file to S3 using presigned URL
      await uploadDocument(url, file, undefined, undefined)

      // Update the specific attachment with S3 key
      setAttachments(prev => prev.map(att =>
        att.id === attachmentId
          ? { ...att, status: 'uploaded', s3Key: key }
          : att
      ))
    } catch (error) {
      console.error('Failed to upload text attachment:', error)
      // Mark the attachment as failed
      setAttachments(prev => prev.map(att =>
        att.id === attachmentId
          ? { ...att, status: 'error' }
          : att
      ))
    }

    // Focus the input
    textareaRef.current?.focus()
  }, [attachments.length, uploadAttachmentMutation])

  // Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    setValue: (newValue) => {
      const trimmedValue = newValue.slice(0, MAX_LENGTH)
      setValue(trimmedValue)
      setCharCount(trimmedValue.length)
      setHasContent(!!trimmedValue.trim())
      // Focus the textarea
      if (textareaRef.current) {
        textareaRef.current.focus()
        // Move cursor to end
        textareaRef.current.setSelectionRange(trimmedValue.length, trimmedValue.length)
      }
    },
    focus: () => {
      textareaRef.current?.focus()
    },
    clear: () => {
      setValue('')
      setCharCount(0)
      setHasContent(false)
      setAttachments([])
      setSending(false)
      // Voice input will stop automatically when cleared via hook
      if (isListening) {
        handleVoiceToggle()
      }
    },
    addTextAttachment
  }), [isListening, handleVoiceToggle, addTextAttachment])

  const handleKeyDown = useCallback((e) => {
    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const isUploading = attachments.some(a => a.status === 'uploading')
      if (value.trim() && !_disable && !isUploading) {
        const messageAttachments = attachments
          .filter(a => a.status === 'uploaded' && a.s3Key)
          .map(a => ({
            title: a.fileName,
            contentType: a.contentType,
            data: {
              key: a.s3Key
            }
          }))

        setSending(true)
        onSend(value, messageAttachments)
      }
    }
  }, [value, _disable, onSend, attachments, setSending])

  const handleSubmit = useCallback(() => {
    const isUploading = attachments.some(a => a.status === 'uploading')
    if (value.trim() && !_disable && !isUploading) {
      // Stop voice recognition if active
      if (isListening) {
        handleVoiceToggle()
      }

      // Build attachments array for the message
      const messageAttachments = attachments
        .filter(a => a.status === 'uploaded' && a.s3Key)
        .map(a => ({
          title: a.fileName,
          contentType: a.contentType,
          data: {
            key: a.s3Key
          }
        }))

      // TODO: Pass attachments to onSend when backend is ready
      // For now, just send the message text

      setSending(true)
      onSend(value, messageAttachments)
    }
  }, [value, _disable, setSending, onSend, attachments, isListening, handleVoiceToggle])

  const handleRemoveAttachment = useCallback((index) => {
    setAttachments(prev => prev.filter((_, idx) => idx !== index))
  }, [])

  const handleRetryAttachment = useCallback(async (index) => {
    // Use functional update to get current attachment from latest state
    let attachment = null
    setAttachments(prev => {
      attachment = prev[index]
      return prev // No-op update just to read current state
    })

    if (!attachment || attachment.status !== 'error') return

    const attachmentId = attachment.id

    // Set back to uploading
    setAttachments(prev => prev.map(att =>
      att.id === attachmentId ? { ...att, status: 'uploading' } : att
    ))

    try {
      // Get presigned URL from backend
      const data = await uploadAttachmentMutation.mutateAsync({
        fileName: attachment.fileName,
        contentType: attachment.contentType
      })

      const { url, key } = data

      // Upload file to S3 using presigned URL
      await uploadDocument(url, attachment.file, undefined, undefined)

      // Update the specific attachment with S3 key
      setAttachments(prev => prev.map(att =>
        att.id === attachmentId
          ? { ...att, status: 'uploaded', s3Key: key }
          : att
      ))
    } catch (error) {
      console.error('Failed to retry upload:', error)
      // Mark the attachment as failed again
      setAttachments(prev => prev.map(att =>
        att.id === attachmentId
          ? { ...att, status: 'error' }
          : att
      ))
    }
  }, [uploadAttachmentMutation])

  // Define accepted file types as array of MIME types
  const acceptedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/*',
    'application/xml',
    'text/xml',
    'application/json',
    'text/json'
  ]

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled,
    onDropAccepted: handleFileChange,
    multiple: true,
    maxFiles: MAX_ATTACHMENTS - attachments.length,
    accept: acceptedFileTypes
  })

  const handleChange = useCallback((e) => {
    const newValue = e.target.value.slice(0, MAX_LENGTH)
    setValue(newValue)
    setCharCount(newValue.length)
    setHasContent(!!newValue.trim())
  }, [])

  const remainingChars = MAX_LENGTH - charCount
  const isNearLimit = remainingChars < 100
  const isUploading = attachments.some(a => a.status === 'uploading')
  const isAtMaxAttachments = attachments.length >= MAX_ATTACHMENTS

  return (
    <FadeIn className={clsx(classes.inputContainer, className)} style={style}>
      <div className={classes.inputContent}>
        <div
          {...getRootProps()}
          className={clsx(classes.inputWrapper, {
            [classes.dragActive]: isDragActive,
            [classes.sending]: sending
          })}
        >
          {attachments.length > 0 && (
            <AttachmentPreviewList
              attachments={attachments}
              onRemove={handleRemoveAttachment}
              onRetry={handleRetryAttachment}
              disabled={sending}
            />
          )}
          <TextareaAutosize
            ref={textareaRef}
            className={classes.textarea}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={sending}
            minRows={minRows}
            maxRows={10}
          />

          <div className={classes.actions}>
            <div className={classes.tools}>
              {isSpeechSupported && (
                <Tooltip title={isListening ? 'Stop voice input' : 'Start voice input'} arrow>
                  <span>
                    <button
                      className={clsx(classes.toolButton, {
                        [classes.toolButtonListening]: isListening
                      })}
                      onClick={handleVoiceToggle}
                      disabled={sending || isUploading}
                      type='button'
                    >
                      <Icon
                        name={isListening ? ICON_NAMES.mic : ICON_NAMES.micNone}
                        style={{ fontSize: '20px', color: 'inherit' }}
                      />
                    </button>
                  </span>
                </Tooltip>
              )}
              <input
                ref={fileInputRef}
                type='file'
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept='.pdf,.jpg,.jpeg,.gif,.png,.webp,text/*,.xml,.json'
                multiple
              />
              <Tooltip title={isAtMaxAttachments ? `Max ${MAX_ATTACHMENTS} files` : 'Attach file'} arrow>
                <span>
                  <button
                    type='button'
                    className={classes.toolButton}
                    onClick={handleFileAttach}
                    disabled={sending || isAtMaxAttachments}
                  >
                    <Icon name='add' style={{ fontSize: '20px', color: 'inherit' }} />
                  </button>
                </span>
              </Tooltip>
            </div>

            <div className={classes.submitSection}>
              <span
                className={classes.charCount} style={{
                  color: isNearLimit ? 'var(--color-warning-500)' : undefined
                }}
              >
                {remainingChars}
              </span>
              <span className={classes.keyboardHint}>
                {window.navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
              </span>
              <SydButton
                size='sm'
                variant='primary'
                onClick={handleSubmit}
                disabled={_disable || !hasContent || isUploading}
                processing={sending}
              >
                <Icon name='navigateNext' style={{ fontSize: '18px', fill: 'var(--color-contrast-primary-500)' }} />
              </SydButton>
            </div>
          </div>
        </div>
      </div>
      {submissionMode === 'external' ? <Disclaimer /> : null}
    </FadeIn>
  )
})

MessageInput.displayName = 'MessageInput'

MessageInput.propTypes = {
  onSend: PropTypes.func.isRequired, // (message: string, attachments?: Array) => void
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  minRows: PropTypes.number,
  submissionMode: PropTypes.oneOf(['inline', 'external'])
}

MessageInput.defaultProps = {
  disabled: false,
  placeholder: 'Type a message...',
  minRows: 1,
  submissionMode: 'inline'
}

export default MessageInput
